import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Activity, Heart, User, Scale } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './BodyFatPercentageCalculatorPage/components/CalculationForm';
import ResultsOverview from './BodyFatPercentageCalculatorPage/components/ResultsOverview';
import BodyCompositionChart from './BodyFatPercentageCalculatorPage/components/BodyCompositionChart';
import QuickAdjustmentSidebar from './BodyFatPercentageCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './BodyFatPercentageCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './BodyFatPercentageCalculatorPage/components/TipsSidebar';
import InfoContent from './BodyFatPercentageCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  gender: 'male',
  age: '30',
  weight: '75',
  height: '175',
  neck: '37',
  waist: '85',
  hip: '0', // Для женщин
  activityLevel: 'moderate',
  measurementMethod: 'navy',
  bodyfatSkinfold: false,
  chest: '0',
  abdomen: '0',
  thigh: '0',
  tricep: '0',
  subscapular: '0',
  suprailiac: '0',
  midaxillary: '0'
};

// Определение уровней активности
export const activityLevels = [
  { id: 'sedentary', name: 'Сидячий образ жизни', description: 'Минимальная активность, работа за столом', factor: 1.2 },
  { id: 'light', name: 'Легкая активность', description: '1-3 тренировки в неделю', factor: 1.375 },
  { id: 'moderate', name: 'Умеренная активность', description: '3-5 тренировок в неделю', factor: 1.55 },
  { id: 'active', name: 'Высокая активность', description: 'Ежедневные тренировки', factor: 1.725 },
  { id: 'very_active', name: 'Очень высокая активность', description: 'Интенсивные тренировки, физическая работа', factor: 1.9 }
];

// Определение методов измерения
export const measurementMethods = [
  { id: 'navy', name: 'Метод ВМС США', description: 'Измерение окружностей тела (шея, талия, бедра)' },
  { id: 'skinfold', name: 'Метод калиперометрии', description: 'Измерение кожных складок специальным инструментом' },
  { id: 'jackson_pollock_3', name: 'Метод Джексона-Поллока (3 точки)', description: 'Упрощенная калиперометрия' },
  { id: 'jackson_pollock_7', name: 'Метод Джексона-Поллока (7 точек)', description: 'Расширенная калиперометрия' }
];

/**
 * BodyFatPercentageCalculatorPage - Полный калькулятор процента жира в организме с различными методами расчета.
 */
const BodyFatPercentageCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('bodyfat-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор процента жира в организме | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте процент жира в организме (BFP) по различным методикам: морские (ВМС США), калиперометрия, Джексона-Поллока. Оцените свой состав тела, получите рекомендации по здоровому диапазону и советы по улучшению показателей.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedMeasurements, setSavedMeasurements] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных измерений из localStorage ---
  useEffect(() => {
    const savedMeasurementsData = localStorage.getItem('savedBodyFatMeasurements');
    if (savedMeasurementsData) {
      try {
        setSavedMeasurements(JSON.parse(savedMeasurementsData));
      } catch (e) {
        console.error("Error loading saved measurements:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'navy':
          newState.measurementMethod = 'navy';
          break;
        case 'skinfold':
          newState.measurementMethod = 'skinfold';
          newState.bodyfatSkinfold = true;
          break;
        case 'jackson-pollock-3':
          newState.measurementMethod = 'jackson_pollock_3';
          newState.bodyfatSkinfold = true;
          break;
        case 'jackson-pollock-7':
          newState.measurementMethod = 'jackson_pollock_7';
          newState.bodyfatSkinfold = true;
          break;
        case 'female':
          newState.gender = 'female';
          newState.weight = '60';
          newState.neck = '32';
          newState.waist = '75';
          newState.hip = '95';
          break;
        case 'male':
          newState.gender = 'male';
          newState.weight = '75';
          newState.neck = '37';
          newState.waist = '85';
          break;
        case 'athlete':
          newState.activityLevel = 'very_active';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});
  }, [subToolId]);

  // Сохранение измерений в localStorage
  const saveMeasurement = (name) => {
    if (!results) return;

    const newSavedMeasurement = {
      id: Date.now().toString(),
      name: name || `Измерение от ${new Date().toLocaleDateString('ru-RU')}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedMeasurements = [...savedMeasurements, newSavedMeasurement];
    setSavedMeasurements(updatedSavedMeasurements);
    localStorage.setItem('savedBodyFatMeasurements', JSON.stringify(updatedSavedMeasurements));

    return newSavedMeasurement;
  };

  // Удаление сохраненного измерения
  const deleteSavedMeasurement = (id) => {
    const updatedSavedMeasurements = savedMeasurements.filter(measurement => measurement.id !== id);
    setSavedMeasurements(updatedSavedMeasurements);
    localStorage.setItem('savedBodyFatMeasurements', JSON.stringify(updatedSavedMeasurements));
  };

  // Загрузка сохраненного измерения
  const loadSavedMeasurement = (id) => {
    const measurementToLoad = savedMeasurements.find(measurement => measurement.id === id);
    if (measurementToLoad) {
      setFormData(measurementToLoad.formData);
      setResults(measurementToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Activity className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор процента жира в организме'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              activityLevels={activityLevels}
              measurementMethods={measurementMethods}
              saveMeasurement={saveMeasurement}
              showAdvancedOptions={showAdvancedOptions}
              setShowAdvancedOptions={setShowAdvancedOptions}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <BodyCompositionChart results={results} />
              </>
            )}

            {/* --- Информационный контент о составе тела --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения измерений --- */}
            <ComparisonSidebar 
              savedMeasurements={savedMeasurements}
              deleteSavedMeasurement={deleteSavedMeasurement}
              loadSavedMeasurement={loadSavedMeasurement}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default BodyFatPercentageCalculatorPage;